/**
 * SPDX-License-Identifier: MPL-2.0
 * Copyright 2021 (c) Moritz Walker, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2023-2024 (c) Daniel Littfinski, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2023-2024 (c) Marc Fischer, ISW University of Stuttgart (for umati and VDW e.V.)
 */
import { createAction, Dispatch } from '@reduxjs/toolkit';
import { Fair } from 'Api/generated';
import Api from 'Api/Api';

export const FairActionTypes = {
  Query: 'Fairs_Query',
  Queried: 'Fairs_Queried',
  Update: 'Fairs_Update',
  Updated: 'Fairs_Updated',
  Create: 'Fairs_Create',
  Created: 'Fairs_Created',
  Delete: 'Fairs_Delete',
  Deleted: 'Fairs_Deleted',
};

export const FairAction = {
  Query: createAction(FairActionTypes.Query),
  Queried: createAction<Fair[]>(FairActionTypes.Queried),
  Update: createAction(FairActionTypes.Update),
  Updated: createAction<Fair>(FairActionTypes.Updated),
  Create: createAction(FairActionTypes.Create),
  Created: createAction<Fair>(FairActionTypes.Created),
  Delete: createAction<Fair>(FairActionTypes.Delete),
  Deleted: createAction<Fair>(FairActionTypes.Deleted),
};

export class FairActions {
  static QueryFairs() {
    return function (dispatch: Dispatch) {
      dispatch(FairAction.Query());
      Api.Fairs.getFairs().then((data) => dispatch(FairAction.Queried(data.data)));
    };
  }

  static CreateFair(fair: Fair) {
    return function (dispatch: Dispatch) {
      dispatch(FairAction.Create());
      Api.Fairs.postFair(fair)
        .then(() => {
          dispatch(FairAction.Created(fair));
        })
        .then(() => FairActions.QueryFairs()(dispatch));
    };
  }

  static UpdateFair(fair: Fair) {
    return function (dispatch: Dispatch) {
      dispatch(FairAction.Update());
      Api.Fairs.putFair(fair.Id!, fair)
        .then(() => {
          dispatch(FairAction.Updated(fair));
        })
        .catch(() => {
          console.error('Could not UpdateFair');
        });
    };
  }

  static DeleteFair(fair: Fair) {
    return function (dispatch: Dispatch) {
      if (fair.Id === undefined) {
        console.error('Delete Fair without ID not possible.');
        return;
      }
      dispatch(FairAction.Delete(fair));

      Api.Fairs.deleteFair(fair.Id)
        .then(() => {
          dispatch(FairAction.Deleted(fair));
        })
        .catch(() => {
          console.error('Could not DeleteFair');
        });
    };
  }

  static UploadLogo(fair: Fair, file: File) {
    return function (dispatch: Dispatch) {
      if (fair.Id != undefined) {
        Api.Fairs.putFairImage(fair.Id, file)
          .then(() => dispatch(FairAction.Updated(fair)))
          .then(() => dispatch(FairAction.Query()));
      }
    };
  }

  static UploadMap(fair: Fair, file: File) {
    return function (dispatch: Dispatch) {
      if (fair.Id != undefined) {
        Api.Fairs.putFairMap(fair.Id, file)
          .then(() => dispatch(FairAction.Updated(fair)))
          .then(() => dispatch(FairAction.Query()));
      }
    };
  }
}
